import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { netMinutes } from "@/lib/shift";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const EMPTY = { code: "", name: "", shift_type: "normal", start_time: "08:00", end_time: "17:00", breaks: [], active: true };

export default function Shifts() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...EMPTY, breaks: [{ name: "Yemek Molası", minutes: 30 }] });
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/shift_definitions").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (n, v) => setForm((f) => ({ ...f, [n]: v }));
  const setBreak = (i, k, v) => setForm((f) => ({ ...f, breaks: f.breaks.map((b, j) => j === i ? { ...b, [k]: v } : b) }));

  const resetForm = () => {
    setEditing(null);
    setForm({ ...EMPTY, breaks: [{ name: "Yemek Molası", minutes: 30 }] });
  };

  const save = async () => {
    if (!form.code || !form.name || !form.start_time || !form.end_time) {
      toast.error("Kod, ad ve çalışma saatleri zorunludur"); return;
    }
    try {
      const payload = { ...form, breaks: form.breaks.map((b) => ({ name: b.name, minutes: Number(b.minutes) || 0 })) };
      if (editing) await api.put(`/shift_definitions/${editing}`, payload);
      else await api.post("/shift_definitions", payload);
      toast.success(editing ? "Vardiya güncellendi" : "Vardiya tanımı kaydedildi — değiştirene kadar tüm girişlerde kullanılacak");
      resetForm();
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const edit = (s) => { setEditing(s.code); setForm({ ...EMPTY, ...s }); };
  const remove = async (s) => {
    if (!window.confirm(`"${s.name}" silinsin mi?`)) return;
    try { await api.delete(`/shift_definitions/${s.code}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="shifts-page" className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Vardiya & Mesai Düzeni</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Çalışma saatleri ile çay/yemek molalarını bir kez tanımlayın — değiştirene kadar tüm üretim girişlerinde net çalışma süresi otomatik hesaplanır.
        </p>
      </div>

      <ShiftForm form={form} set={set} setBreak={setBreak} editing={editing} onSave={save} onCancel={resetForm} />

      <ShiftList items={items} onEdit={edit} onRemove={remove} />
    </div>
  );
}

function ShiftList({ items, onEdit, onRemove }) {
  return (
    <Card className="bg-card border-border overflow-hidden" data-testid="shift-list-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              {["Kod", "Ad", "Tip", "Saatler", "Molalar", "Net Süre", "Durum", ""].map((h) => (
                <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.code} className="border-border" data-testid={`shift-row-${s.code}`}>
                <TableCell className="text-sm py-2.5 font-mono">{s.code}</TableCell>
                <TableCell className="text-sm py-2.5 font-medium">{s.name}</TableCell>
                <TableCell className="text-sm py-2.5">
                  <Badge variant="outline" className="border-border">{s.shift_type === "vardiya" ? "Vardiya" : "Normal Mesai"}</Badge>
                </TableCell>
                <TableCell className="text-sm py-2.5 font-mono">{s.start_time} — {s.end_time}</TableCell>
                <TableCell className="text-xs py-2.5 text-muted-foreground">
                  {(s.breaks || []).map((b) => `${b.name} (${b.minutes}dk)`).join(", ") || "-"}
                </TableCell>
                <TableCell className="text-sm py-2.5 font-mono font-bold">{netMinutes(s)} dk</TableCell>
                <TableCell className="text-sm py-2.5">{s.active === false ? <Badge variant="outline" className="text-muted-foreground">Pasif</Badge> : <Badge className="bg-emerald-600/20 text-emerald-400 border-0 hover:bg-emerald-600/20">Aktif</Badge>}</TableCell>
                <TableCell className="py-2.5 text-right">
                  <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`shift-edit-${s.code}`} onClick={() => onEdit(s)}><Pencil size={14} /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" data-testid={`shift-delete-${s.code}`} onClick={() => onRemove(s)}><Trash2 size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Vardiya tanımı yok</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
  );
}

function ShiftForm({ form, set, setBreak, editing, onSave, onCancel }) {
  return (
    <Card className="p-5 bg-card border-border" data-testid="shift-form-card">
      <h3 className="text-lg font-medium mb-4">{editing ? `Düzenle: ${editing}` : "Yeni Vardiya / Mesai Tanımı"}</h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Kod *</Label>
          <Input value={form.code} onChange={(e) => set("code", e.target.value)} placeholder="Örn: GUNDUZ" data-testid="shift-field-code" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Ad *</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Örn: Gündüz Mesaisi" data-testid="shift-field-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Düzen Tipi</Label>
          <Select value={form.shift_type} onValueChange={(v) => set("shift_type", v)}>
            <SelectTrigger data-testid="shift-field-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="normal" data-testid="shift-type-normal">Normal Mesai</SelectItem>
              <SelectItem value="vardiya" data-testid="shift-type-vardiya">Vardiya</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Başlangıç *</Label>
          <Input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid="shift-field-start" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Bitiş *</Label>
          <Input type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} data-testid="shift-field-end" />
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs">Molalar (çay / yemek)</Label>
          <Button variant="outline" size="sm" className="h-7 text-xs" data-testid="shift-add-break"
            onClick={() => set("breaks", [...form.breaks, { name: "Çay Molası", minutes: 10 }])}>
            <Plus size={13} className="mr-1" /> Mola Ekle
          </Button>
        </div>
        <div className="space-y-2 max-w-lg">
          {form.breaks.map((b, i) => (
            <div key={`${b.name}-${b.minutes}`} className="flex items-center gap-2" data-testid={`shift-break-row-${i}`}>
              <Input value={b.name} onChange={(e) => setBreak(i, "name", e.target.value)} className="h-8 text-sm" data-testid={`shift-break-name-${i}`} />
              <Input type="number" value={b.minutes} onChange={(e) => setBreak(i, "minutes", e.target.value)} className="h-8 w-24 text-sm" data-testid={`shift-break-minutes-${i}`} />
              <span className="text-xs text-muted-foreground">dk</span>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" data-testid={`shift-break-remove-${i}`}
                onClick={() => set("breaks", form.breaks.filter((_, j) => j !== i))}>
                <X size={13} />
              </Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2" data-testid="shift-net-preview">
          Net çalışma süresi: <span className="font-mono font-bold text-foreground">{netMinutes(form)} dk</span>
        </p>
      </div>

      <div className="flex items-center gap-2 mt-4">
        <Button onClick={onSave} data-testid="shift-save-button">{editing ? "Güncelle" : "Kaydet"}</Button>
        {editing && <Button variant="outline" data-testid="shift-cancel-edit" onClick={onCancel}>Vazgeç</Button>}
      </div>
    </Card>
  );
}
